import React from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Divider, Chip, Grid, useTheme
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { 
  Clock, CreditCard, DollarSign, CalendarDays, 
  CheckCircle, AlertTriangle, Info
} from 'lucide-react';

const PayoutDetailsModal = ({ open, onClose, payout }) => {
  const theme = useTheme();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy, h:mm a');
    } catch (e) {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusDetails = (status) => {
    const statusMap = {
      'paid': { 
        color: theme.palette.success.main, 
        icon: <CheckCircle size={20} color={theme.palette.success.main} />,
        text: 'Paid',
        description: 'The payout has been completed and funds should be in your account.'
      },
      'pending': { 
        color: theme.palette.warning.main, 
        icon: <Clock size={20} color={theme.palette.warning.main} />,
        text: 'Pending',
        description: 'The payout is being processed and will be sent soon.'
      },
      'in_transit': { 
        color: theme.palette.info.main, 
        icon: <Clock size={20} color={theme.palette.info.main} />,
        text: 'In Transit',
        description: 'The payout has been initiated and is on its way to your bank account.'
      },
      'failed': { 
        color: theme.palette.error.main, 
        icon: <AlertTriangle size={20} color={theme.palette.error.main} />,
        text: 'Failed',
        description: 'The payout failed to process. Please check your bank details.'
      },
      'canceled': { 
        color: theme.palette.text.secondary, 
        icon: <AlertTriangle size={20} color={theme.palette.text.secondary} />,
        text: 'Canceled',
        description: 'The payout was canceled.'
      }
    };
    
    return statusMap[status] || {
      color: theme.palette.text.secondary,
      icon: <Info size={20} color={theme.palette.text.secondary} />,
      text: status,
      description: 'Current status of the payout.'
    };
  };

  const statusDetails = getStatusDetails(payout.status);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Payout Details
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Box sx={{ 
            width: 40, 
            height: 40, 
            borderRadius: '50%', 
            bgcolor: `${statusDetails.color}15`, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mr: 2
          }}>
            {statusDetails.icon}
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {formatCurrency(payout.amount)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip 
                label={statusDetails.text} 
                size="small" 
                sx={{ 
                  bgcolor: `${statusDetails.color}15`, 
                  color: statusDetails.color,
                  fontWeight: 500
                }} 
              />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                {statusDetails.description}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary">
              Payout Information
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <DollarSign size={16} color={theme.palette.text.secondary} />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                Amount
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              {formatCurrency(payout.amount)}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarDays size={16} color={theme.palette.text.secondary} />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                Created On
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(payout.created_at)}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CalendarDays size={16} color={theme.palette.text.secondary} />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                Arrival Date
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(payout.arrival_date) || 'Estimated 1-2 business days'}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <CreditCard size={16} color={theme.palette.text.secondary} />
              <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                Destination
              </Typography>
            </Box>
            <Typography variant="body1" fontWeight={500}>
              {payout.bank_name || 'Bank Account'} •••• {payout.bank_last4 || '****'}
            </Typography>
          </Grid>
          
          {payout.failure_code && (
            <Grid item xs={12}>
              <Box sx={{ mt: 2, p: 2, bgcolor: `${theme.palette.error.main}15`, borderRadius: 1 }}>
                <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                  Failure Reason
                </Typography>
                <Typography variant="body2">
                  {payout.failure_message || 'Unknown error occurred during processing'}
                </Typography>
                {payout.failure_code && (
                  <Typography variant="caption" color="textSecondary">
                    Error code: {payout.failure_code}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mt: 2, mb: 1 }}>
              Payment Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Transaction ID
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              {payout.id}
            </Typography>
          </Grid>
          
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">
              Payment Method
            </Typography>
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
              {payout.method === 'bank_account' ? 'Bank Transfer' : payout.method}
            </Typography>
          </Grid>
          
          {payout.description && (
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Description
              </Typography>
              <Typography variant="body2">
                {payout.description}
              </Typography>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {payout.status === 'failed' && (
          <Button 
            variant="contained" 
            color="primary"
            onClick={onClose}
          >
            Contact Support
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PayoutDetailsModal;